import { useState, useEffect, useMemo } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Settings as SettingsIcon, Save, Plus, Building2, Bell, Lock, Palette, Upload, X, UtensilsCrossed, LayoutList, Eye, EyeOff, LayoutDashboard, ShoppingBag, Users, Star, BarChart3, UserCog, Info, Grid3x3, Trash2, Edit2, FileText, FileSpreadsheet, Settings2 } from 'lucide-react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { settingsService } from '../../services/settings.service';
import { customersService } from '../../services/customers.service';
import { tablesService, RestaurantTable } from '../../services/tables.service';
import { useOrders } from '../../hooks/useOrders';
import { useMenuTypes } from '../../hooks/useMenuTypes';
import { useCategories } from '../../hooks/useCategories';
import { usePageTitle } from '../../hooks/usePageTitle';
// import { categories } from '../../data/categories';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { SuccessModal } from '../shared/SuccessModal';
import { BulkUploadModal } from './BulkUploadModal';
import { ProductsImportExport } from './ProductsImportExport';
import { MenuTypesSection } from './MenuTypesSection';
import { toast } from 'sonner';
import { CustomerTypesModal } from '../customers/CustomerTypesModal';
import { CustomerType, customerTypesService } from '@/services/customerTypes.service';

interface SettingsPageProps {
  onNavigate?: (page: string) => void;
}

type SettingsTab = 'general' | 'menuTypes' | 'notifications' | 'security' | 'appearance' | 'sidebar' | 'tables' | 'products' | 'customerTypes';

const SETTINGS_TABS: SettingsTab[] = ['general', 'menuTypes', 'products', 'notifications', 'security', 'appearance', 'sidebar', 'tables', 'customerTypes'];

const getInitialSettingsTab = (): SettingsTab => {
  const savedTab = localStorage.getItem('settingsActiveTab') as SettingsTab | null;
  if (savedTab && SETTINGS_TABS.includes(savedTab)) {
    return savedTab;
  }
  return 'general';
};

export function SettingsPage({ onNavigate }: SettingsPageProps) {
  usePageTitle('Settings');

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<SettingsTab>(getInitialSettingsTab);
  const [focusedMenuTypeId, setFocusedMenuTypeId] = useState<string>(() => localStorage.getItem('settingsFocusMenuTypeId') || '');
  const [focusedCategoryId, setFocusedCategoryId] = useState<string>(() => localStorage.getItem('settingsFocusCategoryId') || '');
  const { restaurantName: contextRestaurantName, restaurantLogo: contextRestaurantLogo, brandColors, setRestaurantName: updateRestaurantName, setRestaurantLogo: updateRestaurantLogo, setBrandColors, resetBrandColors, orderByTable: contextOrderByTable, setOrderByTable: setContextOrderByTable } = useRestaurant();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const { orders: ordersData, stats: ordersStats } = useOrders({ page: 1, limit: 50 });
  const { menuTypes, loading: menuTypesLoading, createMenuType, deleteMenuType } = useMenuTypes(user?.tenantId || user?.restaurantId);
  const { categories: allCategories, loading: categoriesLoading, createCategory, deleteCategory, refresh: refreshCategories } = useCategories();

  // General Settings State
  const [restaurantName, setRestaurantNameLocal] = useState(contextRestaurantName);
  const [restaurantLogo, setRestaurantLogoLocal] = useState<string | null>(contextRestaurantLogo);
  const [restaurantLogoFile, setRestaurantLogoFile] = useState<File | null>(null);
  const [restaurantEmail, setRestaurantEmail] = useState('info@restaurant.com');
  const [restaurantPhone, setRestaurantPhone] = useState('+1 234 567 8900');
  const [address, setAddress] = useState('123 Main Street, City, Country');
  const [timezone, setTimezone] = useState('GMT+0');
  const [currency, setCurrency] = useState('KES');
  const [orderByTable, setOrderByTable] = useState(contextOrderByTable);
  const [isRestaurant, setIsRestaurant] = useState(true);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [loyaltyPointsRate, setLoyaltyPointsRate] = useState(10);
  const [loyaltyPointsUnit, setLoyaltyPointsUnit] = useState(1000);
  const [loyaltyTierSilverMin, setLoyaltyTierSilverMin] = useState(100);
  const [loyaltyTierGoldMin, setLoyaltyTierGoldMin] = useState(500);
  const [loyaltyTierPlatinumMin, setLoyaltyTierPlatinumMin] = useState(1000);
  const loyaltyPointsPerUnitPreview = Math.floor((loyaltyPointsUnit * loyaltyPointsRate) / 100);
  const [showTypesModal, setShowTypesModal] = useState(false);
  const [availableTypes, setAvailableTypes] = useState<CustomerType[]>([]);


  const handleIsRestaurantToggle = (value: boolean) => {
    setIsRestaurant(value);
    setSidebarMenuVisibility((prev) => ({
      ...prev,
      orders: value ? true : false,
      menus: value ? true : false,
    }));
    if (!value && !isSuperAdmin && ['menuTypes', 'products', 'tables'].includes(activeTab)) {
      setActiveTab('general');
    }
  };
  const [vatTaxRate, setVatTaxRate] = useState(16); // Default VAT rate 16%
  const [vatInclusive, setVatInclusive] = useState(false); // VAT Inclusive pricing
  const [defaultPrintFormat, setDefaultPrintFormat] = useState<'standard' | 'thermal'>('standard'); // Default print format

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [orderNotifications, setOrderNotifications] = useState(true);
  const [reviewNotifications, setReviewNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);

  // Appearance Settings
  const [language, setLanguage] = useState('en');

  // Sidebar Visibility Settings - Initialize with defaults
  const [sidebarMenuVisibility, setSidebarMenuVisibility] = useState({
    dashboard: true,
    orders: true,
    menus: true,
    customers: true,
    reviews: true,
    messaging:true,
    analytics: true,
    reports: true,
    users: true,
    settings: true,
  });

  // Add Category Modal State
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('🍽️');

  // Bulk Upload State
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState<'categories' | 'menuItems'>('categories');
  // const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Menu Type Modal State
  const [showAddMenuTypeModal, setShowAddMenuTypeModal] = useState(false);
  const [newMenuTypeName, setNewMenuTypeName] = useState('');
  const [newMenuTypeDescription, setNewMenuTypeDescription] = useState('');
  const [newMenuTypeIcon, setNewMenuTypeIcon] = useState('🍽️');
  
  // Expanded Menu Types State
  const [_expandedMenuTypes, setExpandedMenuTypes] = useState<Set<string>>(new Set());
  const [selectedMenuTypeForUpload, setSelectedMenuTypeForUpload] = useState<string>('');

  // Tables State
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [showAddTableModal, setShowAddTableModal] = useState(false);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [newTableName, setNewTableName] = useState('');
  const [newTableCapacity, setNewTableCapacity] = useState(2);
  const [newTableActive, setNewTableActive] = useState(true);
  const [isSubmittingTable, setIsSubmittingTable] = useState(false);

  const sortedTables = useMemo(
    () => [...tables].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })),
    [tables]
  );

  // Modal states
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [_isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoadingSettings(true);
        const response = await settingsService.getTenantSettings();
        console.log('Loaded settings from API:', response.data);
        if (response.data) {
          if (response.data.name) {
            setRestaurantNameLocal(response.data.name);
            updateRestaurantName(response.data.name);
          }
          if (response.data.logo !== undefined) {
            setRestaurantLogoLocal(response.data.logo || null);
            setRestaurantLogoFile(null);
            updateRestaurantLogo(response.data.logo || null);
          }
          if (
            response.data.primaryColor ||
            response.data.primaryHoverColor ||
            response.data.primaryLightColor ||
            response.data.secondaryColor ||
            response.data.accentColor ||
            response.data.successColor ||
            response.data.warningColor ||
            response.data.dangerColor
          ) {
            setBrandColors({
              ...brandColors,
              primary: response.data.primaryColor || brandColors.primary,
              primaryHover: response.data.primaryHoverColor || brandColors.primaryHover,
              primaryLight: response.data.primaryLightColor || brandColors.primaryLight,
              secondary: response.data.secondaryColor || brandColors.secondary,
              accent: response.data.accentColor || brandColors.accent,
              success: response.data.successColor || brandColors.success,
              warning: response.data.warningColor || brandColors.warning,
              danger: response.data.dangerColor || brandColors.danger,
            });
          }
          // Update state with loaded settings
          if (response.data.orderByTable !== undefined) {
            console.log('Setting orderByTable to:', response.data.orderByTable);
            setOrderByTable(response.data.orderByTable);
            // IMPORTANT: Also update the context so MenuTypesPage can read it
            setContextOrderByTable(response.data.orderByTable);
          }
          if (response.data.isRestaurant !== undefined) {
            setIsRestaurant(response.data.isRestaurant);
          }
          if (response.data.loyaltyEnabled !== undefined) {
            setLoyaltyEnabled(response.data.loyaltyEnabled);
          }
          if (response.data.loyaltyPointsRate !== undefined) {
            setLoyaltyPointsRate(response.data.loyaltyPointsRate);
          }
          if (response.data.loyaltyPointsUnit !== undefined) {
            setLoyaltyPointsUnit(response.data.loyaltyPointsUnit);
          }
          if (response.data.loyaltyTierSilverMin !== undefined) {
            setLoyaltyTierSilverMin(response.data.loyaltyTierSilverMin);
          }
          if (response.data.loyaltyTierGoldMin !== undefined) {
            setLoyaltyTierGoldMin(response.data.loyaltyTierGoldMin);
          }
          if (response.data.loyaltyTierPlatinumMin !== undefined) {
            setLoyaltyTierPlatinumMin(response.data.loyaltyTierPlatinumMin);
          }

          if (response.data.timezone) {
            setTimezone(response.data.timezone);
          }
          if (response.data.currency) {
            setCurrency(response.data.currency);
          }
          if (response.data.taxRate !== undefined) {
            setVatTaxRate(response.data.taxRate);
          }
          if (response.data.vatInclusive !== undefined) {
            setVatInclusive(response.data.vatInclusive);
          }
          if (response.data.defaultPrintFormat) {
            setDefaultPrintFormat(response.data.defaultPrintFormat);
          }
          // Do not force-apply theme when opening settings.
          // ThemeContext already hydrates global theme and user may have changed it locally.
          if (response.data.email) {
            setRestaurantEmail(response.data.email);
          }
          if (response.data.phone) {
            setRestaurantPhone(response.data.phone);
          }
          if (response.data.address) {
            setAddress(response.data.address);
            localStorage.setItem('restaurantAddress', response.data.address);
          }
          if (response.data.sidebarMenuVisibility) {
            setSidebarMenuVisibility(response.data.sidebarMenuVisibility);
            // Also update localStorage as cache
            localStorage.setItem('sidebarMenuVisibility', JSON.stringify(response.data.sidebarMenuVisibility));
          }
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Don't show error toast on mount, just use defaults
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, [user?.tenantId, user?.restaurantId]);

  // Load tables on mount
  useEffect(() => {
    const loadTables = async () => {
      try {
        setIsLoadingTables(true);
        const response = await tablesService.getTables();
        if (response.success && response.data) {
          setTables(response.data);
        }
      } catch (error) {
        console.error('Failed to load tables:', error);
        toast.error('Failed to load tables');
      } finally {
        setIsLoadingTables(false);
      }
    };

    loadTables();
  }, [user?.tenantId, user?.restaurantId]);

  // Sync context orderByTable to local state
  useEffect(() => {
    setOrderByTable(contextOrderByTable);
  }, [contextOrderByTable]);

  const handleSaveSettings = () => {
    setShowSaveConfirmation(true);
  };

  const handleConfirmSave = async () => {
    try {
      console.log('Saving settings:', {
        isRestaurant,
        orderByTable,
        taxRate: vatTaxRate,
        vatInclusive,
        defaultPrintFormat,
        timezone,
        currency,
        restaurantEmail,
        restaurantPhone,
        address,
        sidebarMenuVisibility,
        loyaltyEnabled,
        loyaltyPointsRate,
        loyaltyPointsUnit,
        loyaltyTierSilverMin,
        loyaltyTierGoldMin,
        loyaltyTierPlatinumMin,
      });
      
      // Save to database (including sidebar visibility)
      const response = await settingsService.updateTenantSettings({
        isRestaurant,
        orderByTable,
        taxRate: vatTaxRate,
        vatInclusive,
        defaultPrintFormat,
        theme,
        timezone,
        currency,
        email: restaurantEmail,
        phone: restaurantPhone,
        address,
        sidebarMenuVisibility,
        loyaltyEnabled,
        loyaltyPointsRate,
        loyaltyPointsUnit,
        loyaltyTierSilverMin,
        loyaltyTierGoldMin,
        loyaltyTierPlatinumMin,
      });

      let persistedRestaurantLogo = restaurantLogo;

      if (restaurantLogoFile) {
        const uploadResponse = await settingsService.uploadLogo(restaurantLogoFile);
        persistedRestaurantLogo = uploadResponse.data?.url || null;
      }

      await settingsService.updateTenantBranding({
        name: restaurantName,
        logo: persistedRestaurantLogo,
        primaryColor: brandColors.primary,
        primaryHoverColor: brandColors.primaryHover,
        primaryLightColor: brandColors.primaryLight,
        secondaryColor: brandColors.secondary,
        accentColor: brandColors.accent,
        successColor: brandColors.success,
        warningColor: brandColors.warning,
        dangerColor: brandColors.danger,
      });

      if (loyaltyEnabled) {
        const recalcResponse = await customersService.recalculateAllLoyalty();
        if (recalcResponse.success && recalcResponse.data) {
          toast.success(`Loyalty tiers recalculated for ${recalcResponse.data.updatedCount} customers`);
        }
      } else {
        await customersService.resetAllLoyaltyPoints();
        toast.success('Loyalty points reset to zero for all customers');
      }
      
      // Update localStorage as cache for immediate UI updates
      localStorage.setItem('sidebarMenuVisibility', JSON.stringify(sidebarMenuVisibility));
      localStorage.setItem('restaurantAddress', address);
      localStorage.setItem('isRestaurant', String(isRestaurant));

      // Dispatch event to notify sidebar of changes
      window.dispatchEvent(new Event('sidebarVisibilityChanged'));
      
      console.log('Settings saved successfully, response:', response);

      // Update context with new values
      updateRestaurantName(restaurantName);
      updateRestaurantLogo(persistedRestaurantLogo);
      setRestaurantLogoLocal(persistedRestaurantLogo);
      setRestaurantLogoFile(null);
      setContextOrderByTable(orderByTable);
      setShowSaveConfirmation(false);
      setSuccessMessage({
        title: 'Settings Saved',
        message: 'Your settings have been updated successfully!'
      });
      setShowSuccessModal(true);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings. Please try again.');
      setShowSaveConfirmation(false);
    }
  };



  const handleAddMenuType = () => {
    setNewMenuTypeName('');
    setNewMenuTypeDescription('');
    setNewMenuTypeIcon('🍽️');
    setShowAddMenuTypeModal(true);
  };

  const handleSaveMenuType = async () => {
    if (!newMenuTypeName.trim()) {
      toast.error('Please enter a menu type name');
      return;
    }

    try {
      const tenantId = user?.tenantId || user?.restaurantId;
      if (!tenantId) {
        toast.error('User tenant ID not found');
        return;
      }
      
      await createMenuType({
        name: newMenuTypeName,
        description: newMenuTypeDescription || undefined,
        icon: newMenuTypeIcon,
        tenantId: tenantId,
      });
      setShowAddMenuTypeModal(false);
      setNewMenuTypeName('');
      setNewMenuTypeDescription('');
      setNewMenuTypeIcon('🍽️');
      setSuccessMessage({
        title: 'Menu Type Added',
        message: `Menu type "${newMenuTypeName}" created successfully!`
      });
      setShowSuccessModal(true);
      toast.success('Menu type created successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create menu type');
    }
  };

  const handleDeleteMenuType = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will also remove all associated categories and items.`)) {
      return;
    }

    try {
      await deleteMenuType(id);
      setSuccessMessage({
        title: 'Menu Type Deleted',
        message: `Menu type "${name}" deleted successfully!`
      });
      setShowSuccessModal(true);
      toast.success('Menu type deleted successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete menu type');
    }
  };

  const toggleMenuType = (menuTypeId: string) => {
    setExpandedMenuTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuTypeId)) {
        newSet.delete(menuTypeId);
      } else {
        newSet.add(menuTypeId);
      }
      return newSet;
    });
  };

  const handleBulkUploadProducts = (menuTypeId: string) => {
    setSelectedMenuTypeForUpload(menuTypeId);
    setUploadType('menuItems');
    setShowBulkUploadModal(true);
  };

  const handleAddCategory = (menuTypeId: string) => {
    setSelectedMenuTypeForUpload(menuTypeId);
    setEditingCategoryId(null);
    setNewCategoryName('');
    setNewCategoryDescription('');
    setNewCategoryIcon('🍽️');
    setShowAddCategoryModal(true);
  };

  const getCategoriesForMenuType = (menuTypeId: string) => {
    return allCategories.filter(cat => cat.menuTypeId === menuTypeId);
  };

  const handleSaveCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await createCategory({
        name: newCategoryName,
        description: newCategoryDescription || undefined,
        menuTypeId: selectedMenuTypeForUpload || undefined,
        isActive: true,
      });
      setShowAddCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryIcon('🍽️');
      await refreshCategories();
      setSuccessMessage({
        title: 'Category Added',
        message: `Category "${newCategoryName}" created successfully!`
      });
      setShowSuccessModal(true);
      toast.success('Category created successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await deleteCategory(id);
      await refreshCategories();
      setSuccessMessage({
        title: 'Category Deleted',
        message: `Category "${name}" deleted successfully!`
      });
      setShowSuccessModal(true);
      toast.success('Category deleted successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete category');
    }
  };

  const handleViewCategoryItems = (categoryId: string, categoryName: string, menuTypeName?: string, menuTypeId?: string) => {
    // Store category data in localStorage for the CategoryProductsPage
    localStorage.setItem('selectedCategoryId', categoryId);
    localStorage.setItem('selectedCategoryName', categoryName);
    if (menuTypeName) {
      localStorage.setItem('selectedMenuTypeName', menuTypeName);
    }
    if (menuTypeId) {
      localStorage.setItem('selectedMenuTypeId', menuTypeId);
    }
    // Navigate to the category products page — use the pattern so App.tsx updates selectedCategoryId state
    onNavigate?.(`category-products-${categoryId}`);
  };

  const handleAddTable = () => {
    setNewTableName('');
    setNewTableCapacity(2);
    setNewTableActive(true);
    setShowAddTableModal(true);
  };

  const handleSaveTable = async () => {
    if (!newTableName.trim()) {
      toast.error('Please enter a table name');
      return;
    }

    if (newTableCapacity < 1) {
      toast.error('Capacity must be at least 1');
      return;
    }

    try {
      setIsSubmittingTable(true);
      const response = await tablesService.createTable({
        name: newTableName,
        capacity: newTableCapacity,
        isActive: newTableActive
      });

      if (response.success && response.data) {
        setTables([...tables, response.data]);
        setShowAddTableModal(false);
        setNewTableName('');
        setNewTableCapacity(2);
        setNewTableActive(true);
        setSuccessMessage({
          title: 'Table Added',
          message: `Table "${newTableName}" created successfully!`
        });
        setShowSuccessModal(true);
        toast.success('Table created successfully!');
      } else {
        toast.error(response.message || 'Failed to create table');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create table');
    } finally {
      setIsSubmittingTable(false);
    }
  };

  const handleDeleteTable = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete table "${name}"?`)) {
      return;
    }

    try {
      const response = await tablesService.deleteTable(id);
      if (response.success) {
        setTables(tables.filter(t => t.id !== id));
        setSuccessMessage({
          title: 'Table Deleted',
          message: `Table "${name}" deleted successfully!`
        });
        setShowSuccessModal(true);
        toast.success('Table deleted successfully!');
      } else {
        toast.error(response.message || 'Failed to delete table');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete table');
    }
  };

  const handleEditTable = (table: RestaurantTable) => {
    setEditingTableId(table.id);
    setNewTableName(table.name);
    setNewTableCapacity(table.capacity);
    setNewTableActive(table.isActive);
    setShowEditTableModal(true);
  };

  const handleUpdateTable = async () => {
    if (!newTableName.trim()) {
      toast.error('Please enter a table name');
      return;
    }

    if (newTableCapacity < 1) {
      toast.error('Capacity must be at least 1');
      return;
    }

    if (!editingTableId) {
      toast.error('No table selected for editing');
      return;
    }

    try {
      setIsSubmittingTable(true);
      const response = await tablesService.updateTable(editingTableId, {
        name: newTableName,
        capacity: newTableCapacity,
        isActive: newTableActive
      });

      if (response.success && response.data) {
        const updatedTable = response.data;
        setTables(tables.map(t => t.id === editingTableId ? updatedTable : t));
        setShowEditTableModal(false);
        setEditingTableId(null);
        setNewTableName('');
        setNewTableCapacity(2);
        setNewTableActive(true);
        setSuccessMessage({
          title: 'Table Updated',
          message: `Table "${newTableName}" updated successfully!`
        });
        setShowSuccessModal(true);
        toast.success('Table updated successfully!');
      } else {
        toast.error(response.message || 'Failed to update table');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update table');
    } finally {
      setIsSubmittingTable(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="settings" 
        onNavigate={onNavigate}
        ordersCount={ordersStats?.total || ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
              <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">Manage your restaurant settings and preferences</p>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
              <button
                onClick={() => setActiveTab('general')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'general'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  General
                </div>
              </button>
              {(isRestaurant || isSuperAdmin) && (
              <button
                onClick={() => setActiveTab('menuTypes')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'menuTypes'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4" />
                  Menu & Categories
                </div>
              </button>
              )}
              {(isRestaurant || isSuperAdmin) && (
              <button
                onClick={() => setActiveTab('products')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'products'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Products
                </div>
              </button>
              )}

              {/* Customer Types Button */}
              <button
                onClick={() => {
                  setActiveTab('customerTypes');
                  setShowTypesModal(true);
                }}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'customerTypes'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >                
                <div className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4" />
                  Customer Types
                </div>
              </button>

              <button
                onClick={() => setActiveTab('notifications')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'notifications'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Notifications
                </div>
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'security'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Security
                </div>
              </button>
              <button
                onClick={() => setActiveTab('appearance')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'appearance'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Appearance
                </div>
              </button>
              <button
                onClick={() => setActiveTab('sidebar')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'sidebar'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <LayoutList className="w-4 h-4" />
                  Sidebar Menu
                </div>
              </button>
              {(isRestaurant || isSuperAdmin) && (
              <button
                onClick={() => setActiveTab('tables')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'tables'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Grid3x3 className="w-4 h-4" />
                  Tables
                </div>
              </button>
              )}
            </div>

            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Restaurant Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload Section */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Restaurant Logo
                    </label>
                    <div className="flex items-center gap-4">
                      {restaurantLogo ? (
                        <div className="relative">
                          <img
                            src={restaurantLogo}
                            alt="Restaurant Logo"
                            className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200 dark:border-gray-700"
                          />
                          <button
                            onClick={() => {
                              setRestaurantLogoLocal(null);
                              setRestaurantLogoFile(null);
                            }}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex items-center justify-center">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setRestaurantLogoFile(file);
                              setRestaurantLogoLocal(URL.createObjectURL(file));
                            }
                          }}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                        >
                          <Upload className="w-4 h-4" />
                          Upload Logo
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Recommended: Square image, at least 200x200px
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Restaurant Name</label>
                    <input
                      type="text"
                      value={restaurantName}
                      onChange={(e) => setRestaurantNameLocal(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={restaurantEmail}
                      onChange={(e) => setRestaurantEmail(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={restaurantPhone}
                      onChange={(e) => setRestaurantPhone(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Address</label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
                    <select
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="GMT+0">GMT+0 (London)</option>
                      <option value="GMT+1">GMT+1 (Paris)</option>
                      <option value="GMT+3">GMT+3 (Nairobi, East Africa)</option>
                      <option value="GMT-5">GMT-5 (New York)</option>
                      <option value="GMT-8">GMT-8 (Los Angeles)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currency</label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="KES">KES (KSh)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="JPY">JPY (¥)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">VAT Tax Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={vatTaxRate}
                      onChange={(e) => setVatTaxRate(parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      placeholder="16.00"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Default tax rate applied to all orders (e.g., 16% VAT)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">VAT Pricing Mode</label>
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Prices Include VAT</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {vatInclusive 
                            ? 'Product prices already include VAT (VAT will be calculated from the total)' 
                            : 'VAT will be added on top of product prices'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setVatInclusive(!vatInclusive)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          vatInclusive ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            vatInclusive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default Print Format</label>
                    <select
                      value={defaultPrintFormat}
                      onChange={(e) => setDefaultPrintFormat(e.target.value as 'standard' | 'thermal')}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="standard">📄 Standard (A4) - 800mm width</option>
                      <option value="thermal">🖨️ Thermal (80mm) - For POS printers</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Default format used when printing receipts. You can still choose a different format when printing.
                    </p>
                  </div>
                </div>

                {/* Order Management Settings */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Order Management</h3>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Is Restaurant</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Enables restaurant features — Orders, Menus and Tables will be shown in the sidebar
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleIsRestaurantToggle(!isRestaurant)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isRestaurant ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          isRestaurant ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mt-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Order by Table</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Allow customers to select a table when placing orders
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOrderByTable(!orderByTable)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        orderByTable ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          orderByTable ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {orderByTable && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-xs text-blue-700 dark:text-blue-400">
                        💡 <strong>Tip:</strong> When enabled, customers will be prompted to select a table number when creating orders. 
                        Make sure to configure your tables in the system before enabling this feature.
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Customer Loyalty Program</h3>

                  {/* Enable / Disable toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Enable Loyalty Program</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Award loyalty points to customers based on their spending
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLoyaltyEnabled(!loyaltyEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        loyaltyEnabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          loyaltyEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {!loyaltyEnabled && (
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        Loyalty program is <strong>disabled</strong>. All customer loyalty points will be reset to zero when you save. Enable it above to configure the settings.
                      </p>
                    </div>
                  )}

                  {loyaltyEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Loyalty Rate (%)</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={loyaltyPointsRate}
                        onChange={(e) => setLoyaltyPointsRate(Math.max(1, parseInt(e.target.value || '1', 10)))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">For Every Amount Spent</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={loyaltyPointsUnit}
                        onChange={(e) => setLoyaltyPointsUnit(Math.max(1, parseInt(e.target.value || '1', 10)))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Example: {loyaltyPointsRate}% of {loyaltyPointsUnit.toLocaleString()} = {loyaltyPointsPerUnitPreview.toLocaleString()} points per full unit spent.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bronze Tier Minimum Points</label>
                      <input
                        type="number"
                        value={0}
                        disabled
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg text-sm cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Default starting tier</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Silver Tier Minimum Points</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={loyaltyTierSilverMin}
                        onChange={(e) => setLoyaltyTierSilverMin(Math.max(0, parseInt(e.target.value || '0', 10)))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gold Tier Minimum Points</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={loyaltyTierGoldMin}
                        onChange={(e) => setLoyaltyTierGoldMin(Math.max(0, parseInt(e.target.value || '0', 10)))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Platinum Tier Minimum Points</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={loyaltyTierPlatinumMin}
                        onChange={(e) => setLoyaltyTierPlatinumMin(Math.max(0, parseInt(e.target.value || '0', 10)))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>
                  </div>
                  )}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveSettings}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Menu Types Settings */}
            {activeTab === 'menuTypes' && (
              <div className="space-y-6">
                {isSuperAdmin && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-700 dark:text-amber-400">
                    <span className="text-base">⚠️</span>
                    <span>This tab is only visible to other tenant roles when <strong>Is Restaurant</strong> is enabled in General settings.</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu Structure</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Organize your menu by types (Breakfast, Lunch, Dinner), then add categories and items within each.
                    </p>
                  </div>
                  <button
                    onClick={handleAddMenuType}
                    className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Menu Type
                  </button>
                </div>

                <MenuTypesSection
                  menuTypes={menuTypes}
                  allCategories={allCategories}
                  loading={menuTypesLoading || categoriesLoading}
                  focusedMenuTypeId={focusedMenuTypeId}
                  focusedCategoryId={focusedCategoryId}
                  onFocusedMenuTypeHandled={() => {
                    setFocusedMenuTypeId('');
                    setFocusedCategoryId('');
                    localStorage.removeItem('settingsFocusMenuTypeId');
                    localStorage.removeItem('settingsFocusCategoryId');
                  }}
                  onAddMenuType={handleAddMenuType}
                  onDeleteMenuType={handleDeleteMenuType}
                  onBulkUploadProducts={handleBulkUploadProducts}
                  onAddCategory={handleAddCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onViewCategoryItems={handleViewCategoryItems}
                />

                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <SettingsIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-1">
                        Quick Guide
                      </h4>
                      <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                        <li>Create menu types (e.g., "Breakfast", "Lunch", "Dinner")</li>
                        <li>Click "Bulk Upload" to import categories and menu items via CSV</li>
                        <li>Or click "Add Category" to add categories manually one by one</li>
                        <li>Expand a menu type to view and manage its categories</li>
                        <li>Click on any category to view and manage its menu items</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Settings */}
            {activeTab === 'notifications' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Notification Preferences</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Email Notifications</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive email notifications for important updates</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailNotifications}
                        onChange={(e) => setEmailNotifications(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">New Order Notifications</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Get notified when new orders are placed</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderNotifications}
                        onChange={(e) => setOrderNotifications(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Review Notifications</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Get notified when customers leave reviews</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reviewNotifications}
                        onChange={(e) => setReviewNotifications(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Marketing Emails</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive promotional and marketing emails</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marketingEmails}
                        onChange={(e) => setMarketingEmails(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveSettings}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Security Settings</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Password</label>
                    <input
                      type="password"
                      placeholder="Enter current password"
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password</label>
                    <input
                      type="password"
                      placeholder="Enter new password"
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Two-Factor Authentication</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add an extra layer of security to your account</p>
                    <button className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium">
                      Enable 2FA
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveSettings}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Update Password
                  </button>
                </div>
              </div>
            )}

            {/* Appearance Settings */}
            {activeTab === 'appearance' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Appearance & Language</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setTheme('light')}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          theme === 'light' ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-white">Light</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Bright and clean</p>
                        </div>
                      </button>
                      <button
                        onClick={() => setTheme('dark')}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          theme === 'dark' ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-white">Dark</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Easy on the eyes</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>

                  {/* Brand Colors Section */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Brand Colors</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Customize your dashboard colors to match your restaurant's brand</p>
                      </div>
                      <button
                        onClick={resetBrandColors}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        Reset to Default
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Primary Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Primary Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.primary}
                            onChange={(e) => setBrandColors({ ...brandColors, primary: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.primary}
                            onChange={(e) => setBrandColors({ ...brandColors, primary: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Main brand color (buttons, links, highlights)</p>
                      </div>

                      {/* Primary Hover Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Primary Hover
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.primaryHover}
                            onChange={(e) => setBrandColors({ ...brandColors, primaryHover: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.primaryHover}
                            onChange={(e) => setBrandColors({ ...brandColors, primaryHover: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Darker shade for hover states</p>
                      </div>

                      {/* Primary Light Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Primary Light
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.primaryLight}
                            onChange={(e) => setBrandColors({ ...brandColors, primaryLight: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.primaryLight}
                            onChange={(e) => setBrandColors({ ...brandColors, primaryLight: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Light tint for backgrounds</p>
                      </div>

                      {/* Secondary Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Secondary Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.secondary}
                            onChange={(e) => setBrandColors({ ...brandColors, secondary: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.secondary}
                            onChange={(e) => setBrandColors({ ...brandColors, secondary: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Secondary accent color</p>
                      </div>

                      {/* Accent Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Accent Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.accent}
                            onChange={(e) => setBrandColors({ ...brandColors, accent: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.accent}
                            onChange={(e) => setBrandColors({ ...brandColors, accent: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Accent color for highlights</p>
                      </div>

                      {/* Success Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Success Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.success}
                            onChange={(e) => setBrandColors({ ...brandColors, success: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.success}
                            onChange={(e) => setBrandColors({ ...brandColors, success: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For positive actions and success messages</p>
                      </div>

                      {/* Warning Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Warning Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.warning}
                            onChange={(e) => setBrandColors({ ...brandColors, warning: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.warning}
                            onChange={(e) => setBrandColors({ ...brandColors, warning: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For warnings and caution messages</p>
                      </div>

                      {/* Danger Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Danger Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={brandColors.danger}
                            onChange={(e) => setBrandColors({ ...brandColors, danger: e.target.value })}
                            className="w-16 h-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={brandColors.danger}
                            onChange={(e) => setBrandColors({ ...brandColors, danger: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For errors and destructive actions</p>
                      </div>
                    </div>

                    {/* Color Preview Section */}
                    <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-2">
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors" style={{ backgroundColor: brandColors.primary }}>
                            Primary Button
                          </button>
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors" style={{ backgroundColor: brandColors.primaryHover }}>
                            Primary Hover
                          </button>
                        </div>
                        <div className="space-y-2">
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: brandColors.secondary }}>
                            Secondary
                          </button>
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: brandColors.accent }}>
                            Accent
                          </button>
                        </div>
                        <div className="space-y-2">
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: brandColors.success }}>
                            Success
                          </button>
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: brandColors.warning }}>
                            Warning
                          </button>
                        </div>
                        <div className="space-y-2">
                          <button className="w-full px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: brandColors.danger }}>
                            Danger
                          </button>
                          <div className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: brandColors.primaryLight, color: brandColors.primary }}>
                            Light BG
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveSettings}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Sidebar Menu Visibility Settings */}
            {activeTab === 'sidebar' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Sidebar Menu Visibility</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Control which menu items appear in the sidebar navigation for all users in your restaurant. Hidden items will not be accessible from the sidebar for any user.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {/* Dashboard */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.dashboard ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <LayoutDashboard className={`w-5 h-5 ${sidebarMenuVisibility.dashboard ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Dashboard</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Overview and analytics</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, dashboard: !sidebarMenuVisibility.dashboard })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.dashboard
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.dashboard ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.dashboard ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Orders */}
                  <div className={`flex items-center justify-between p-4 rounded-lg border ${
                    !isRestaurant ? 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.orders && isRestaurant ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <ShoppingBag className={`w-5 h-5 ${sidebarMenuVisibility.orders && isRestaurant ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Orders</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {!isRestaurant ? 'Hidden — enable "Is Restaurant" to unlock' : 'Manage customer orders'}
                        </p>
                      </div>
                    </div>
                    <button
                      disabled={!isRestaurant}
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, orders: !sidebarMenuVisibility.orders })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        !isRestaurant
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          : sidebarMenuVisibility.orders
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.orders && isRestaurant ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.orders && isRestaurant ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Menus */}
                  <div className={`flex items-center justify-between p-4 rounded-lg border ${
                    !isRestaurant ? 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.menus && isRestaurant ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <UtensilsCrossed className={`w-5 h-5 ${sidebarMenuVisibility.menus && isRestaurant ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Menus</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {!isRestaurant ? 'Hidden — enable "Is Restaurant" to unlock' : 'Menu items and categories'}
                        </p>
                      </div>
                    </div>
                    <button
                      disabled={!isRestaurant}
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, menus: !sidebarMenuVisibility.menus })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        !isRestaurant
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                          : sidebarMenuVisibility.menus
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.menus && isRestaurant ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.menus && isRestaurant ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Customers */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.customers ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <Users className={`w-5 h-5 ${sidebarMenuVisibility.customers ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Customers</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Customer management and tables</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, customers: !sidebarMenuVisibility.customers })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.customers
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.customers ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.customers ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Reviews */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.reviews ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <Star className={`w-5 h-5 ${sidebarMenuVisibility.reviews ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Reviews</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Customer feedback and ratings</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, reviews: !sidebarMenuVisibility.reviews })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.reviews
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.reviews ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.reviews ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Messaging */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.messaging ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <Star className={`w-5 h-5 ${sidebarMenuVisibility.messaging ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Messaging</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Customer communication and support</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, messaging: !sidebarMenuVisibility.messaging })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.messaging
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.messaging ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.messaging ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.analytics ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <BarChart3 className={`w-5 h-5 ${sidebarMenuVisibility.analytics ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Analytics</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Reports and insights</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, analytics: !sidebarMenuVisibility.analytics })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.analytics
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.analytics ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.analytics ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Users */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.users ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <UserCog className={`w-5 h-5 ${sidebarMenuVisibility.users ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Users</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">User management</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, users: !sidebarMenuVisibility.users })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.users
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.users ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.users ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Reports */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sidebarMenuVisibility.reports ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <FileText className={`w-5 h-5 ${sidebarMenuVisibility.reports ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Reports</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Exportable order and tax reports</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarMenuVisibility({ ...sidebarMenuVisibility, reports: !sidebarMenuVisibility.reports })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        sidebarMenuVisibility.reports
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {sidebarMenuVisibility.reports ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {sidebarMenuVisibility.reports ? 'Visible' : 'Hidden'}
                    </button>
                  </div>

                  {/* Settings - Always visible, cannot be hidden */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 opacity-75">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                        <SettingsIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">Settings</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">System settings (Always visible)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                      <Eye className="w-4 h-4" />
                      Always Visible
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-3">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                        Tenant-wide settings - applies to all users
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                        These visibility settings will apply to all users in your restaurant. Changes take effect immediately after saving.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveSettings}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Tables Management */}
            {activeTab === 'tables' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                {isSuperAdmin && (
                  <div className="flex items-center gap-3 px-4 py-3 mb-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-700 dark:text-amber-400">
                    <span className="text-base">⚠️</span>
                    <span>This tab is only visible to other tenant roles when <strong>Is Restaurant</strong> is enabled in General settings.</span>
                  </div>
                )}

                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Restaurant Tables</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Create and manage dining tables in your restaurant
                    </p>
                  </div>
                  <button
                    onClick={handleAddTable}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Table
                  </button>
                </div>

                {isLoadingTables ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500 dark:text-gray-400">Loading tables...</div>
                  </div>
                ) : tables.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Grid3x3 className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-2">No tables created yet</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">Start by adding your first table</p>
                    <button
                      onClick={handleAddTable}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add First Table
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sortedTables.map((table) => (
                      <div
                        key={table.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">{table.name}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Capacity: {table.capacity} {table.capacity === 1 ? 'person' : 'people'}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            table.isActive
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                          }`}>
                            {table.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => handleEditTable(table)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTable(table.id, table.name)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'products' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                {isSuperAdmin && (
                  <div className="flex items-center gap-3 px-4 py-3 mb-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-700 dark:text-amber-400">
                    <span className="text-base">⚠️</span>
                    <span>This tab is only visible to other tenant roles when <strong>Is Restaurant</strong> is enabled in General settings.</span>
                  </div>
                )}
                <ProductsImportExport />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        uploadType={uploadType}
        menuTypeId={selectedMenuTypeForUpload}
        onUploadComplete={() => {
          // Refresh categories after upload
          refreshCategories();
          // Close the modal
          setShowBulkUploadModal(false);
        }}
      />

      {/* Add Menu Type Modal */}
      {showAddMenuTypeModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowAddMenuTypeModal(false)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Add New Menu Type
                  </h2>
                  <button
                    onClick={() => setShowAddMenuTypeModal(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Menu Type Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMenuTypeName}
                    onChange={(e) => setNewMenuTypeName(e.target.value)}
                    placeholder="e.g., Breakfast, Lunch, Dinner"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newMenuTypeDescription}
                    onChange={(e) => setNewMenuTypeDescription(e.target.value)}
                    placeholder="Optional description"
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Icon (Emoji)
                  </label>
                  <input
                    type="text"
                    value={newMenuTypeIcon}
                    onChange={(e) => setNewMenuTypeIcon(e.target.value)}
                    placeholder="🍽️"
                    maxLength={2}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Choose an emoji to represent this menu type
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setShowAddMenuTypeModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMenuType}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Create Menu Type
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowAddCategoryModal(false)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {editingCategoryId ? 'Edit Category' : 'Add New Category'}
                  </h2>
                  <button
                    onClick={() => setShowAddCategoryModal(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {selectedMenuTypeForUpload && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      📝 Adding to: <strong>{menuTypes.find(mt => mt.id === selectedMenuTypeForUpload)?.name || 'Selected Menu Type'}</strong>
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="e.g., Pizza, Burgers, Desserts"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    placeholder="Describe this category..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Icon (Emoji)
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center text-3xl">
                      {newCategoryIcon}
                    </div>
                    <input
                      type="text"
                      value={newCategoryIcon}
                      onChange={(e) => setNewCategoryIcon(e.target.value)}
                      placeholder="🍽️"
                      maxLength={2}
                      className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Choose an emoji that represents this category
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
                <button
                  onClick={() => setShowAddCategoryModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategory}
                  disabled={!newCategoryName.trim()}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingCategoryId ? 'Update Category' : 'Add Category'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Save Settings Confirmation Modal */}
      {showSaveConfirmation && (
        <ConfirmationModal
          title="Save Settings"
          message="Are you sure you want to save these settings?"
          confirmText="Save Changes"
          onConfirm={handleConfirmSave}
          onClose={() => setShowSaveConfirmation(false)}
          icon={<Save className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          title={successMessage.title}
          message={successMessage.message}
          onClose={() => setShowSuccessModal(false)}
        />
      )}

      {/* Add Table Modal */}
      {showAddTableModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowAddTableModal(false)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Table</h2>
                  <button
                    onClick={() => setShowAddTableModal(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Table Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTableName}
                    onChange={(e) => setNewTableName(e.target.value)}
                    placeholder="e.g., Table 1, Window Seat"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Capacity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newTableCapacity}
                    onChange={(e) => setNewTableCapacity(parseInt(e.target.value) || 1)}
                    placeholder="Number of seats"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <input
                    type="checkbox"
                    id="table-active"
                    checked={newTableActive}
                    onChange={(e) => setNewTableActive(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                  />
                  <label htmlFor="table-active" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    Table is active and available
                  </label>
                </div>
              </div>
              <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
                <button
                  onClick={() => setShowAddTableModal(false)}
                  disabled={isSubmittingTable}
                  className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTable}
                  disabled={!newTableName.trim() || isSubmittingTable}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingTable ? 'Creating...' : 'Create Table'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Table Modal */}
      {showEditTableModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowEditTableModal(false)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Table</h2>
                  <button
                    onClick={() => setShowEditTableModal(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Table Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTableName}
                    onChange={(e) => setNewTableName(e.target.value)}
                    placeholder="e.g., Table 1, Window Seat"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Capacity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newTableCapacity}
                    onChange={(e) => setNewTableCapacity(parseInt(e.target.value) || 1)}
                    placeholder="Number of seats"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <input
                    type="checkbox"
                    id="table-edit-active"
                    checked={newTableActive}
                    onChange={(e) => setNewTableActive(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                  />
                  <label htmlFor="table-edit-active" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    Table is active and available
                  </label>
                </div>
                {!newTableActive && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-xs text-yellow-800 dark:text-yellow-300">
                      ⚠️ Inactive tables will not appear when selecting tables in menu orders
                    </p>
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
                <button
                  onClick={() => setShowEditTableModal(false)}
                  disabled={isSubmittingTable}
                  className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTable}
                  disabled={!newTableName.trim() || isSubmittingTable}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingTable ? 'Updating...' : 'Update Table'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {showTypesModal && (
        <CustomerTypesModal
          onClose={() => {
            setShowTypesModal(false);
            customerTypesService.getAll().then((r) => { if (r.success && r.data) setAvailableTypes(r.data); });
          }}
        />
      )}
    </div>
  );
}